import { useCallback, useMemo } from "react";
import client from "libs/client";
import queryClient from "libs/queryClient";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import {
	DiscoverGameInlinePatchField,
	getDiscoverGamesColumns,
	getDiscoverGamesFilters,
} from "../../../../datagrid-configs/discoverGames";
import { Permission, Resource } from "pages/productManagement/types";
import { getClientErrorMessage, getClientErrorStatus } from "common/src/utils/errorUtils";
import { mapDropdownOptions } from "../helpers/mapDropdownOptions";
import type { DiscoverGamePatchRequest, GameStatus } from "libs/apiClient";
import { useUserData } from "features/userData/context";

type DiscoverGameInlinePatchValue = {
	[K in DiscoverGameInlinePatchField]: DiscoverGamePatchRequest[K];
};

export function useDiscoverGamesColumnConfig(locale: string, onGameAnalysisOpen?: (gameId: number) => void) {
	const { addErrorToast, addSuccessToast } = useToast();
	const userData = useUserData();

	const { data: clubMetadataDropdownOptionsResponse } = client.getDiscoverGamesV2ClubMetadataUserOptions.useQuery({});
	const { data: assigneeDropdownOptionsResponse } = client.getDiscoverGamesV2AssigneeOptions.useQuery({});
	const { data: gameStatusFilterOptions } = client.getDiscoverGamesV2GameStatusOptions.useQuery({});

	const patchDiscoverGameMutation = client.patchDiscoverGame.useMutation({
		onSuccess: async () => {
			await queryClient.invalidateQueries(["getDiscoverGamesDynamic"]);
			// Refresh the manager/assignee filter dropdowns so a just-set manager/assignee shows up
			// immediately (e.g. after setting a club manager) instead of only after a page reload.
			await queryClient.invalidateQueries(["getDiscoverGamesV2FilterOptions"]);
			addSuccessToast(_("message.status.update.success"));
		},
		onError: (error: unknown) => {
			const status = getClientErrorStatus(error);
			if (status === 403) {
				return;
			}
			if (status !== null && status >= 400 && status < 500) {
				const message = getClientErrorMessage(error) ?? _("discover.gameDetail.toast.updateFailed");
				addErrorToast(message);
			}
		},
	});

	const patchDiscoverGameField = useCallback(
		<Field extends DiscoverGameInlinePatchField>(
			gameId: number,
			field: Field,
			value: DiscoverGameInlinePatchValue[Field],
		) => {
			patchDiscoverGameMutation.mutate({
				id: gameId,
				body: {
					id: gameId,
					requestedFields: [field],
					[field]: value,
				} as DiscoverGamePatchRequest,
			});
		},
		[patchDiscoverGameMutation],
	);

	const handlePriorityChange = useCallback(
		(gameId: number, priority: number) =>
			patchDiscoverGameField(gameId, DiscoverGameInlinePatchField.Priority, priority),
		[patchDiscoverGameField],
	);
	const handleGameStatusChange = useCallback(
		(gameId: number, gameStatus: string | null) =>
			patchDiscoverGameField(
				gameId,
				DiscoverGameInlinePatchField.GameStatus,
				gameStatus as GameStatus | null,
			),
		[patchDiscoverGameField],
	);
	const handleClubManagerChange = useCallback(
		(gameId: number, managerId: number | null) =>
			patchDiscoverGameField(gameId, DiscoverGameInlinePatchField.ClubManagerId, managerId),
		[patchDiscoverGameField],
	);
	const handleContentManagerChange = useCallback(
		(gameId: number, contentManagerId: number | null) =>
			patchDiscoverGameField(gameId, DiscoverGameInlinePatchField.ContentManagerId, contentManagerId),
		[patchDiscoverGameField],
	);
	const handleAssigneeChange = useCallback(
		(gameId: number, assigneeId: number | null) =>
			patchDiscoverGameField(gameId, DiscoverGameInlinePatchField.AssigneeId, assigneeId),
		[patchDiscoverGameField],
	);
	const handleIsVipChange = useCallback(
		(gameId: number, isVip: boolean) =>
			patchDiscoverGameField(gameId, DiscoverGameInlinePatchField.IsVip, isVip),
		[patchDiscoverGameField],
	);

	const clubMetadataDropdownOptions = useMemo(
		() => mapDropdownOptions(clubMetadataDropdownOptionsResponse?.data),
		[clubMetadataDropdownOptionsResponse],
	);
	const assigneeDropdownOptions = useMemo(
		() => mapDropdownOptions(assigneeDropdownOptionsResponse?.data),
		[assigneeDropdownOptionsResponse],
	);
	const gameStatusRowOptions = useMemo(
		() => mapDropdownOptions(gameStatusFilterOptions?.data),
		[gameStatusFilterOptions],
	);

	const gameUploadsPermissions = useMemo(
		() => {
			const hasPermission = (resource: string, permission: string) => (
				userData.isSuperAdmin ||
				(userData.permissions?.some(
					(userPermission) =>
						(userPermission.resource === resource && userPermission.permission === permission) ||
						(userPermission.resource === "all" && userPermission.permission === "all"),
				) ?? false)
			);

			return {
				canEditGameUploads: hasPermission(Resource.GAME_UPLOADS, Permission.EDIT),
				canEditAssignee: hasPermission(Resource.GAME_UPLOADS, Permission.GAME_ASSIGNEE_SELECTION),
				canEditGameStatus: hasPermission(Resource.GAME_UPLOADS, Permission.GAME_STATUS_EDIT),
				canEditVip: hasPermission(Resource.GAME_UPLOAD_SINGLE, Permission.GAME_VIP),
				canViewSla: hasPermission(Resource.GAME_UPLOADS, Permission.GAME_VIEW_SLA),
			};
		},
		[userData.isSuperAdmin, userData.permissions],
	);
	const { canEditGameUploads, canEditAssignee, canEditGameStatus, canEditVip, canViewSla } = gameUploadsPermissions;
	const canEditPriority = canEditGameUploads;

	const discoverGamesColumns = useMemo(
		() =>
			getDiscoverGamesColumns({
				clubManagerOptions: clubMetadataDropdownOptions,
				contentManagerOptions: clubMetadataDropdownOptions,
				assigneeOptions: assigneeDropdownOptions,
				gameStatusOptions: gameStatusRowOptions,
				onClubManagerChange: canEditGameUploads ? handleClubManagerChange : undefined,
				onContentManagerChange: canEditGameUploads ? handleContentManagerChange : undefined,
				onAssigneeChange: canEditAssignee ? handleAssigneeChange : undefined,
				onPriorityChange: canEditPriority ? handlePriorityChange : undefined,
				onGameStatusChange: canEditGameStatus ? handleGameStatusChange : undefined,
				onIsVipChange: canEditVip ? handleIsVipChange : undefined,
				onGameAnalysisOpen,
				isClubMetadataUpdating: patchDiscoverGameMutation.isLoading,
				canEditGameUploads,
				canEditAssignee,
				canEditGameStatus,
				canEditPriority,
				canEditVip,
				canViewSla,
			}),
		[
			locale,
			clubMetadataDropdownOptions,
			assigneeDropdownOptions,
			gameStatusRowOptions,
			handleClubManagerChange,
			handleContentManagerChange,
			handleAssigneeChange,
			handlePriorityChange,
			handleGameStatusChange,
			handleIsVipChange,
			onGameAnalysisOpen,
			patchDiscoverGameMutation.isLoading,
			gameUploadsPermissions,
			canEditPriority,
			canEditVip,
		],
	);
	const discoverGamesFilters = useMemo(() => getDiscoverGamesFilters(), [locale]);

	return {
		clubMetadataDropdownOptions,
		assigneeDropdownOptions,
		gameStatusRowOptions,
		canEditGameUploads,
		canEditAssignee,
		canEditGameStatus,
		canEditPriority,
		discoverGamesColumns,
		discoverGamesFilters,
	};
}
